export type MentionableBoardNode = {
	uid: string;
	label?: string | null;
};

const NODE_REFERENCE_PATTERN = /\[\[node:([^|\]]+)(?:\|[^\]]*)?\]\]/gi;
const WORD_CHARACTER_PATTERN = /[\p{L}\p{N}]/u;

const normalizeForMatching = (value: string): string =>
	value
		.normalize('NFKC')
		.replace(/[\u2018\u2019]/g, "'")
		.replace(/[\u201c\u201d]/g, '"')
		.toLocaleLowerCase();

const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const findLabelMentionIndex = (content: string, label: string): number => {
	const normalizedLabel = normalizeForMatching(label.trim());
	if (!normalizedLabel) return -1;

	const startsWithWordCharacter = WORD_CHARACTER_PATTERN.test(normalizedLabel[0] || '');
	const endsWithWordCharacter = WORD_CHARACTER_PATTERN.test(
		normalizedLabel[normalizedLabel.length - 1] || ''
	);
	const flexibleLabel = escapeRegex(normalizedLabel).replace(/\s+/g, '\\s+');
	const prefix = startsWithWordCharacter ? '(^|[^\\p{L}\\p{N}])' : '';
	const suffix = endsWithWordCharacter ? '(?=$|[^\\p{L}\\p{N}])' : '';
	const match = new RegExp(`${prefix}${flexibleLabel}${suffix}`, 'iu').exec(content);
	if (match) {
		return match.index + (startsWithWordCharacter ? (match[1] || '').length : 0);
	}

	// Flowy sometimes shortens long labels in prose (for example,
	// "Royal authority..."). Treat an explicit ellipsis after at least two
	// label words as a mention without making ordinary partial-word matches.
	const labelWords = normalizedLabel.split(/\s+/).filter(Boolean);
	for (let wordCount = labelWords.length - 1; wordCount >= 2; wordCount -= 1) {
		const abbreviatedLabel = labelWords.slice(0, wordCount).join(' ');
		if (abbreviatedLabel.length < 8) continue;
		const flexibleAbbreviation = escapeRegex(abbreviatedLabel).replace(/\s+/g, '\\s+');
		const abbreviatedMatch = new RegExp(
			`${prefix}${flexibleAbbreviation}(?=\\s*(?:…|\\.{3}))`,
			'iu'
		).exec(content);
		if (abbreviatedMatch) {
			return (
				abbreviatedMatch.index +
				(startsWithWordCharacter ? (abbreviatedMatch[1] || '').length : 0)
			);
		}
	}

	return -1;
};

/**
 * Returns board node ids in the order Flowy mentions them. Explicit node markers
 * and response node references are included, while label matching uses word
 * boundaries so a node such as "art" does not match "start".
 */
export const findMentionedBoardNodeIds = (
	content: string,
	nodes: MentionableBoardNode[],
	explicitNodeIds: string[] = []
): string[] => {
	const normalizedContent = normalizeForMatching(content || '');
	const candidates: Array<{ uid: string; index: number; labelLength: number; order: number }> = [];
	const referencedNodeIds = new Set(explicitNodeIds.filter(Boolean));
	let order = 0;

	NODE_REFERENCE_PATTERN.lastIndex = 0;
	let markerMatch: RegExpExecArray | null;
	while ((markerMatch = NODE_REFERENCE_PATTERN.exec(content || '')) !== null) {
		referencedNodeIds.add(markerMatch[1].trim());
		candidates.push({
			uid: markerMatch[1].trim(),
			index: markerMatch.index,
			labelLength: Number.MAX_SAFE_INTEGER,
			order: order++,
		});
	}

	for (const node of nodes) {
		const label = typeof node.label === 'string' ? node.label.trim() : '';
		if (!node.uid || !label) continue;
		const index = findLabelMentionIndex(normalizedContent, label);
		if (index < 0) continue;
		candidates.push({ uid: node.uid, index, labelLength: label.length, order: order++ });
	}

	for (const uid of explicitNodeIds) {
		if (!uid) continue;
		candidates.push({
			uid,
			index: Number.MAX_SAFE_INTEGER,
			labelLength: 0,
			order: order++,
		});
	}

	candidates.sort(
		(a, b) => a.index - b.index || b.labelLength - a.labelLength || a.order - b.order
	);

	const knownNodeIds = new Set([
		...nodes.map((node) => node.uid),
		...referencedNodeIds,
	]);
	const seen = new Set<string>();
	return candidates
		.map((candidate) => candidate.uid)
		.filter((uid) => knownNodeIds.has(uid) && !seen.has(uid) && Boolean(seen.add(uid)));
};
